import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Paper,
  InputAdornment,
  Autocomplete,
  Breadcrumbs,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Home as HomeIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  SportsKabaddi as CoachIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useAuth } from '../../context/AuthContext';
import { coachingSessionAPI, userAPI, amenityAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { CoachingSession, CreateCoachingSessionData } from '../../types/coaching';
import { formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import ReservationResultsDialog from './ReservationResultsDialog';
import { User, UserRole } from '@/types';

interface Coach {
  id: string;
  name: string;
  email: string;
}

const EditCoachingSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Determine if we're creating a new session or editing an existing one
  const isEditing = Boolean(sessionId);
  
  // State
  const [sessionData, setSessionData] = useState<CoachingSession | null>(null);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete confirmation dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Reservation result dialog state
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);

  const DEFAULT_SESSION_DURATION = 30; // in minutes

  // Form state
  const [formData, setFormData] = useState(() => {
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + DEFAULT_SESSION_DURATION);
    
    return {
      name: '',
      description: '',
      coachId: '',
      isGuestCoach: false,
      guestCoachName: '',
      venues: [] as string[],
      level: '',
      capacity: 4,
      sessionDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      minBookingDurationMinutes: DEFAULT_SESSION_DURATION,
      memberRate: { cost: '', duration: '' },
      guestRate: { cost: '', duration: '' },
      allowGuestBookings: true,
      notes: '',
      image: ''
    };
  });

  // Collapsible section states
  const [pricingConfigExpanded, setPricingConfigExpanded] = useState(false);

  // Helper function to parse time strings that might include timezone info
  const parseTimeForEdit = (timeString: string): Date => {
    if (!timeString) return new Date();
    const timeOnly = timeString.split('+')[0].split('-')[0];
    const [hours, minutes, seconds] = timeOnly.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds || 0, 0);
    return date;
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchCoaches();
        await fetchAmenities();

        if (isEditing && sessionId) {
          await fetchSessionData();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId, isEditing]);

  const fetchSessionData = async () => {
    if (!sessionId) return;

    try {
      const response = await coachingSessionAPI.getCoachingSessionById(sessionId);
      const session = response.data;
      setSessionData(session);

      // Populate form with existing session data
      setFormData({
        name: session.name,
        description: session.description || '',
        coachId: session.coachId || '',
        isGuestCoach: session.isGuestCoach || false,
        guestCoachName: session.guestCoachName || '',
        venues: session.venues || [],
        level: session.level,
        capacity: session.capacity,
        sessionDate: parseDateOnlyString(session.sessionDate),
        startTime: parseTimeForEdit(session.startTime),
        endTime: parseTimeForEdit(session.endTime),
        minBookingDurationMinutes: session.minBookingDurationMinutes,
        memberRate: {
          cost: session.memberRateCost?.toString() || '',
          duration: session.memberRateDuration?.toString() || session.minBookingDurationMinutes.toString()
        },
        guestRate: {
          cost: session.guestRateCost?.toString() || '',
          duration: session.guestRateDuration?.toString() || session.minBookingDurationMinutes.toString()
        },
        allowGuestBookings: session.allowGuestBookings,
        notes: session.notes || '',
        image: session.image || ''
      });
    } catch (error: any) {
      console.error('Error fetching session:', error);
      setError('Failed to load session data');
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await userAPI.getCoaches();
      setCoaches(response.data);
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
      setError('Failed to fetch coaches');
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      setAmenities(response.data);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      // Don't set error state for amenities - it's optional
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    
    // Validation
    if (!formData.name.trim()) {
      setError('Session name is required');
      setSubmitting(false);
      return;
    }
    
    if (!formData.isGuestCoach && !formData.coachId) {
      setError('Please select a coach or enable guest coach option');
      setSubmitting(false);
      return;
    }
    
    if (formData.isGuestCoach && !formData.guestCoachName.trim()) {
      setError('Guest coach name is required');
      setSubmitting(false);
      return;
    }
    
    // Validate time logic
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      setSubmitting(false);
      return;
    }

    try {
      const startTime = formData.startTime.toTimeString().split(' ')[0].slice(0, 5);
      const endTime = formData.endTime.toTimeString().split(' ')[0].slice(0, 5);

      const sessionDataToSend: CreateCoachingSessionData = {
        name: formData.name,
        description: formData.description,
        coachId: formData.isGuestCoach ? null : formData.coachId,
        isGuestCoach: formData.isGuestCoach,
        guestCoachName: formData.isGuestCoach ? formData.guestCoachName : undefined,
        venues: formData.venues,
        level: formData.level,
        capacity: formData.capacity,
        sessionDate: formatDateForInput(formData.sessionDate),
        startTime,
        endTime,
        minBookingDurationMinutes: formData.minBookingDurationMinutes,
        memberRate: formData.memberRate.cost ? {
          cost: parseFloat(formData.memberRate.cost),
          duration: parseInt(formData.memberRate.duration) || formData.minBookingDurationMinutes
        } : undefined,
        guestRate: formData.guestRate.cost ? {
          cost: parseFloat(formData.guestRate.cost),
          duration: parseInt(formData.guestRate.duration) || formData.minBookingDurationMinutes
        } : undefined,
        allowGuestBookings: formData.allowGuestBookings,
        notes: formData.notes,
        image: formData.image
      };

      const operationType = isEditing ? 'update' : 'create';
      const response = isEditing && sessionId
        ? await coachingSessionAPI.updateCoachingSession(sessionId, sessionDataToSend)
        : await coachingSessionAPI.createCoachingSession(sessionDataToSend);

      // Show reservation results dialog if there's a result
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityType: 'session',
          entityName: formData.name,
          operationType,
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      } else {
        setSuccessMessage(`Coaching session ${operationType}d successfully!`);
        // Navigate back after short delay
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      }
      
    } catch (error: any) {
      console.error('Error saving session:', error);
      setError(error.response?.data?.message || 'Failed to save session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!isEditing || !sessionId) return;
    setOpenDeleteDialog(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionId) return;

    try {
      await coachingSessionAPI.deleteCoachingSession(sessionId);
      setOpenDeleteDialog(false);
      setSuccessMessage('Coaching session deleted successfully!');
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      console.error('Error deleting session:', error);
      setError(error.response?.data?.message || 'Failed to delete session');
      setOpenDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.COACH, UserRole.EMPLOYEE, UserRole.SUPER_ADMIN]}>
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Button
              component={RouterLink}
              to="/coaching/sessions"
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Coaching Sessions
            </Button>
            <Typography color="text.primary">
              {isEditing ? 'Edit Session' : 'Create Session'}
            </Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <CoachIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {isEditing ? 'Edit Coaching Session' : 'Create New Coaching Session'}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                variant="outlined"
              >
                Cancel
              </Button>
              {isEditing && (
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  color="error"
                  variant="outlined"
                >
                  Delete
                </Button>
              )}
              <Button
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                variant="contained"
                disabled={submitting || !formData.name || (!formData.isGuestCoach && !formData.coachId) || (formData.isGuestCoach && !formData.guestCoachName)}
              >
                {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')} Session
              </Button>
            </Stack>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Main Form */}
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* Session Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Session Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Autocomplete
                    options={coaches}
                    getOptionLabel={(option: User | string) => {
                      if (typeof option === 'string') return option;
                      if (!option) return '';
                      const firstName = option.firstName || '';
                      const lastName = option.lastName || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      if (fullName) return fullName;
                      return option.email || 'Unknown Coach';
                    }}
                    value={coaches.find(coach => coach.id === formData.coachId) || null}
                    onChange={(event, newValue) => {
                      setFormData({ 
                        ...formData, 
                        coachId: newValue?.id || '',
                        guestCoachName: newValue ? (newValue.firstName && newValue.lastName ? `${newValue.firstName} ${newValue.lastName}` : newValue.email || 'Unknown Coach') : formData.guestCoachName
                      });
                    }}
                    disabled={formData.isGuestCoach}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coach"
                        required={!formData.isGuestCoach}
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? '' : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.guestCoachName}
                      onChange={(e) => setFormData({ ...formData, guestCoachName: e.target.value })}
                      fullWidth
                      required
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              {/* Venues */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  multiple
                  options={amenities}
                  getOptionLabel={(option) => `${option.name} (${option.type})`}
                  getOptionKey={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={amenities.filter(a => formData.venues.includes(a.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, venues: newValue.map(a => a.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Venues"
                      placeholder="Select one or more venues/courts (optional)"
                      helperText="Leave empty if no venue reservation needed"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...chipProps}
                          size="small"
                        />
                      );
                    })
                  }
                />
              </Grid>

              {/* Level */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  fullWidth
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                />
              </Grid>

              {/* Session Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Session Date"
                  value={formData.sessionDate}
                  onChange={(date) => setFormData({ ...formData, sessionDate: date || new Date() })}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true
                    } 
                  }}
                />
              </Grid>

              {/* Capacity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  fullWidth
                  required
                  inputProps={{ min: 1, max: 20 }}
                />
              </Grid>

              {/* Start Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(time) => setFormData({ ...formData, startTime: time || new Date() })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* End Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="End Time"
                  value={formData.endTime}
                  onChange={(time) => setFormData({ ...formData, endTime: time || new Date() })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* Min Booking Duration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Minimum Booking Duration (minutes)"
                  type="number"
                  value={formData.minBookingDurationMinutes}
                  onChange={(e) => {
                    const newDuration = parseInt(e.target.value) || 30;
                    setFormData({ 
                      ...formData, 
                      minBookingDurationMinutes: newDuration,
                      // Update rate durations if they're empty or match the previous min duration
                      memberRate: {
                        ...formData.memberRate,
                        duration: !formData.memberRate.duration || formData.memberRate.duration === formData.minBookingDurationMinutes.toString() 
                          ? newDuration.toString() 
                          : formData.memberRate.duration
                      },
                      guestRate: {
                        ...formData.guestRate,
                        duration: !formData.guestRate.duration || formData.guestRate.duration === formData.minBookingDurationMinutes.toString() 
                          ? newDuration.toString() 
                          : formData.guestRate.duration
                      }
                    });
                  }}
                  fullWidth
                  inputProps={{ min: 15, max: 240 }}
                  helperText="Minimum time slot members can book"
                />
              </Grid>

              {/* Allow Guest Bookings */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowGuestBookings}
                      onChange={(e) => setFormData({ ...formData, allowGuestBookings: e.target.checked })}
                    />
                  }
                  label="Allow Guest Bookings"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            {/* Collapsible Pricing Configuration */}
            <Accordion expanded={pricingConfigExpanded} onChange={() => setPricingConfigExpanded(!pricingConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Pricing Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Member Rate */}
                  <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Member Rate</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Cost"
                          type="number"
                          value={formData.memberRate.cost}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            memberRate: { ...formData.memberRate, cost: e.target.value }
                          })}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Cost for members"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Duration (minutes)"
                          type="number"
                          value={formData.memberRate.duration}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            memberRate: { ...formData.memberRate, duration: e.target.value }
                          })}
                          fullWidth
                          inputProps={{ min: 15, max: 480 }}
                          helperText={`Default: ${formData.minBookingDurationMinutes} min`}
                          placeholder={formData.minBookingDurationMinutes.toString()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Guest Rate */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" gutterBottom>Guest Rate</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Cost"
                          type="number"
                          value={formData.guestRate.cost}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            guestRate: { ...formData.guestRate, cost: e.target.value }
                          })}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Cost for guests"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Duration (minutes)"
                          type="number"
                          value={formData.guestRate.duration}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            guestRate: { ...formData.guestRate, duration: e.target.value }
                          })}
                          fullWidth
                          inputProps={{ min: 15, max: 480 }}
                          helperText={`Default: ${formData.minBookingDurationMinutes} min`}
                          placeholder={formData.minBookingDurationMinutes.toString()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              placeholder="Internal notes about this session"
            />
          </Paper>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} disableEscapeKeyDown={true}>
          <DialogTitle>Delete Coaching Session</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{formData.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDeleteSession} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reservation Results Dialog */}
        <ReservationResultsDialog
          open={reservationDialogOpen}
          onClose={() => {
            setReservationDialogOpen(false);
            setReservationDialogData(null);
            // Navigate back to management page after viewing reservation results
            navigate(-1);
          }}
          entityType={reservationDialogData?.entityType || 'session'}
          entityName={reservationDialogData?.entityName || ''}
          operationType={reservationDialogData?.operationType || 'create'}
          reservationResult={reservationDialogData?.reservationResult || null}
        />
        </AdminAccessControl>
      </Container>
    </LocalizationProvider>
  );
};

export default EditCoachingSessionPage;